const API_BASE = "https://uktomars.co.uk/api";
const TOKEN_KEY = "authToken";

export const getAuthToken = () => localStorage.getItem(TOKEN_KEY);

export const setAuthToken = (token) => {
  if (token) {
    localStorage.setItem(TOKEN_KEY, token);
  } else {
    localStorage.removeItem(TOKEN_KEY);
  }
};

const authHeaders = (token, { json = false } = {}) => {
  const headers = {
    Accept: "application/json",
    Authorization: `Bearer ${token}`,
  };
  if (json) {
    headers["Content-Type"] = "application/json";
  }
  return headers;
};

export const fetchCurrentUser = async () => {
  const token = getAuthToken();
  if (!token) return null;

  const response = await fetch(`${API_BASE}/auth/me`, {
    headers: authHeaders(token),
  });

  if (!response.ok) {
    if (response.status === 401) {
      setAuthToken(null);
    }
    throw new Error("Unable to load profile.");
  }

  const payload = await response.json();
  return payload?.data ?? null;
};

const normalizeUserPayload = (payload) => {
  if (!payload || typeof payload !== "object") return null;
  if (payload.data && typeof payload.data === "object") return payload.data;
  if (payload.email || payload.name) return payload;
  return null;
};

export const updateCurrentUser = async ({
  name,
  country_code,
  phone,
  company,
  county,
}) => {
  const token = getAuthToken();
  if (!token) {
    throw new Error("Please sign in to update your profile.");
  }

  const response = await fetch(`${API_BASE}/me`, {
    method: "PATCH",
    headers: authHeaders(token, { json: true }),
    body: JSON.stringify({ name, country_code, phone, company, county }),
  });

  const payload = await response.json().catch(() => ({}));

  if (!response.ok) {
    if (response.status === 401) {
      setAuthToken(null);
    }
    const validationMessage = payload?.errors
      ? Object.values(payload.errors).flat().join(" ")
      : null;
    throw new Error(
      validationMessage ||
        payload?.message ||
        payload?.error ||
        "Unable to update profile.",
    );
  }

  const updatedUser = normalizeUserPayload(payload);
  if (updatedUser) return updatedUser;

  return fetchCurrentUser();
};

export const extractAuthToken = (payload) =>
  payload?.token ?? payload?.data?.token ?? payload?.access_token ?? null;

export async function signInWithCredentials({ email, password }) {
  const response = await fetch(`${API_BASE}/auth/login`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify({ email, password }),
  });

  const payload = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(
      payload?.message ||
        payload?.error ||
        "Unable to sign in. Please check your details and try again.",
    );
  }

  const token = extractAuthToken(payload);
  if (!token) {
    throw new Error("Sign in succeeded but no access token was returned.");
  }

  setAuthToken(token);
  return token;
}
